import React from 'react';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import getBrandSpecificValue from '@rover/shared/js/utils/getBrandSpecificValue';

// Brand resolution happens at call time, so these stay functions rather than plain
// descriptors — a plain const would pick a brand at module load.
export const MODAL_HEADING_TEXT = (): MessageDescriptor =>
  getBrandSpecificValue<MessageDescriptor>({
    rover: msg`What is RoverProtect?`,
    catInAFlat: msg`Safety is at the heart of everything we do`,
  });

export const MODAL_SUB_HEADING_TEXT = (): JSX.Element => (
  <Trans>
    <strong>
      Every booking is backed by tools to help keep you and your pet safe. You’ll have:
    </strong>
  </Trans>
);
export const SCREENED_PET_SITTERS_TEXT = (): JSX.Element => (
  <Trans>
    <strong>Screened pet sitters</strong> have already passed a third-party background check and
    show verified reviews from other pet parents, like you.
  </Trans>
);
export const MESSAGE_PHOTO_TEXT = (): JSX.Element => (
  <Trans>
    <strong>Messaging & photo updates</strong> from your sitter during each stay.
  </Trans>
);

export const ROVER_GUARANTEE_TEXT = (veterinaryCareLimit: string): JSX.Element => (
  <Trans>
    <strong>The Rover Guarantee</strong> can protect you and your pet for up to{' '}
    {veterinaryCareLimit} in eligible vet care.
  </Trans>
);

export const HOME_HEADING_TEXT: MessageDescriptor = msg`RoverProtect`;
export const HOME_SUB_HEADING_TEXT: MessageDescriptor = msg`Find peace of mind with every booking.`;
export const HOME_SUBMIT_BUTTON_TEXT: MessageDescriptor = msg`Book a local sitter`;

export const HOMEPAGE_ROVERPROTECT = (): JSX.Element => (
  <Trans>
    Stay in touch with photos and messaging. Plus, your booking is backed by RoverProtect, including
    24/7 support and reimbursement for eligible vet care.
  </Trans>
);

export const HOMEPAGE_REVIEWS = (): JSX.Element => (
  <Trans>
    Read verified reviews by pet parents like you and choose a screened sitter who’s a great match
    for you and your pets.
  </Trans>
);
export const SUPPORT_TEXT = (): JSX.Element =>
  getBrandSpecificValue({
    rover: (
      <Trans>
        <strong>24/7 support</strong> from the Rover team–here to help if you ever need someone to
        talk to.
      </Trans>
    ),
    catInAFlat: <Trans>Our support team is here to help with any questions you may have</Trans>,
  });
export const LEARN_MORE_BUTTON_TEXT = (): MessageDescriptor =>
  getBrandSpecificValue<MessageDescriptor>({
    rover: msg`Learn more about RoverProtect`,
    catInAFlat: msg`Learn more about the Rover Guarantee`,
  });
export const MODAL_SUBMIT_BUTTON_TEXT: MessageDescriptor = msg`Got it`;
export const LEARN_MORE_TEXT: MessageDescriptor = msg`Learn more`;
export const LEARN_MORE_HREF = (): string =>
  getBrandSpecificValue({
    rover: '/rover-protect',
    catInAFlat: '/rover-guarantee',
  });

export const MEMBER_PROFILE_SIGNAL_TEXT = (): JSX.Element =>
  getBrandSpecificValue({
    rover: (
      <Trans>
        Each sitter joining Rover needs to pass a background check. And by booking on Rover, you can
        get photo & message updates, 24/7 support, and reimbursement for eligible vet care.
      </Trans>
    ),
    catInAFlat: (
      <Trans>
        Each sitter joining needs to pass an identity verification. And by booking on Cat in a Flat,
        you can get photo & message updates, 24/7 support and reimbursement for eligible vet care.
      </Trans>
    ),
  });

export const CHECKOUT_SIGNAL_TEXT = (): JSX.Element => (
  <Trans>
    This booking is backed by RoverProtect. You can get photo & message updates from your sitter,
    24/7 support, and reimbursement for eligible vet care.
  </Trans>
);

export const CHECKOUT_SIGNAL_CIAF_TEXT = (): JSX.Element => (
  <Trans>
    This booking is backed by the Rover Guarantee. You can get photo & message updates from your
    sitter, 24/7 support, and reimbursement for eligible vet care
  </Trans>
);

export const CONVERSATION_SIGNAL_TEXT = (): JSX.Element => (
  <Trans>
    <strong>This booking is backed by RoverProtect.</strong> You can get photo & message updates
    from your sitter, 24/7 support, and reimbursement for eligible vet care.
  </Trans>
);

export const CHECKOUT_CIAF_SIGNAL_TEXT = (): JSX.Element => (
  <Trans>
    This booking is backed by the Rover Guarantee. You can get photo & message updates from your
    sitter, 24/7 support, and reimbursement for eligible vet care.
  </Trans>
);

// Survey string
export const SURVEY_NEXT_BUTTON_TEXT: MessageDescriptor = msg`Next`;
export const SURVEY_SUBMIT_BUTTON_TEXT: MessageDescriptor = msg`Submit`;
